import {
  dictationClock,
  DICTATION_MAX_SECONDS,
  insertDictation,
  PAUSED,
  WRITING_ACTIONS,
  type WritingAction,
} from "../../server/rules/ai";
import type { Me } from "../shell/types";
import { el, toast } from "./dom";
import { icon } from "./icons";

// The mic and the AI menu on every multi-line text box in the app (brief 1B-8, mockup v10
// "dictation + AI writing, added to every textarea"). The shell starts this once; every textarea
// any screen draws gets them while the company has the feature on:
// - the mic: speak for up to 10 minutes and the words land at the cursor. The clock counts up,
//   turns amber at 9 minutes and red for the last 30 seconds with a short beep, then stops at
//   10:00. Press the mic again to keep going; the next part is added after it. The recording is
//   sent once for the words and never kept;
// - the AI menu: Clean up, Shorten, Draft a reply (where the box answers a conversation), Rewrite
//   as a clinical note. AI's version shows beside the original with Use it and Discard; nothing
//   changes until Use it.
// With a feature off (or not set up), its button isn't there; at the limit with pause, it says
// "AI paused until next month" and the box works by hand as always.

type States = Me["ai"];
let states: States = { dictation: "off", writing: "off", reading: "off" };

/** The section the person is in, for the call log (Settings › AI spend by module). */
const moduleHere = () => location.pathname.split("/").filter(Boolean)[0] ?? "home";

const shows = (s: States[keyof States]) => s === "on" || s === "paused";

// ---- Dictation --------------------------------------------------------------------------------------

interface Recording {
  box: HTMLTextAreaElement;
  button: HTMLButtonElement;
  recorder: MediaRecorder;
  stream: MediaStream;
  chunks: Blob[];
  started: number;
  timer: ReturnType<typeof setInterval>;
  /** Where the words go: the cursor when it started (or after the last part). */
  caret: number;
}
let recording: Recording | null = null;
/** Where the next part goes in each box: after the words the last part added. */
const nextAt = new WeakMap<HTMLTextAreaElement, number>();

function beep() {
  try {
    const ctx = new AudioContext();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.value = 880;
    g.gain.value = 0.08;
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.18);
    o.onended = () => void ctx.close();
  } catch {
    // No sound where audio isn't allowed; the red clock still shows.
  }
}

function showClock(r: Recording) {
  const seconds = (Date.now() - r.started) / 1000;
  const c = dictationClock(seconds);
  const dt = r.button.querySelector(".dt");
  if (dt) dt.textContent = c.text;
  r.button.classList.toggle("amber", c.level === "amber");
  r.button.classList.toggle("red", c.level === "red");
  if (c.beep && !r.button.dataset.beeped) {
    r.button.dataset.beeped = "1";
    beep();
  }
  if (c.stop) stopRecording();
}

async function startRecording(box: HTMLTextAreaElement, button: HTMLButtonElement) {
  if (recording) stopRecording();
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch {
    return toast("The microphone isn't available", "Allow the mic for this site, or type it");
  }
  const type = ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/mp4"].find(
    (t) => typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(t),
  );
  // A low speech bit rate keeps 10 minutes inside one request.
  const recorder = new MediaRecorder(stream, {
    ...(type ? { mimeType: type } : {}),
    audioBitsPerSecond: 24_000,
  });
  const r: Recording = {
    box,
    button,
    recorder,
    stream,
    chunks: [],
    started: Date.now(),
    timer: setInterval(() => showClock(r), 250),
    caret: nextAt.get(box) ?? box.selectionStart ?? box.value.length,
  };
  recorder.ondataavailable = (e) => {
    if (e.data.size) r.chunks.push(e.data);
  };
  recorder.onstop = () => void finish(r);
  recorder.start();
  recording = r;
  button.classList.add("rec");
  button.setAttribute("aria-pressed", "true");
  button.setAttribute("aria-label", "Stop dictating");
  showClock(r);
  toast("Listening…", "Press the mic again to stop · up to 10 minutes");
}

function stopRecording() {
  const r = recording;
  if (!r) return;
  recording = null;
  clearInterval(r.timer);
  if (r.recorder.state !== "inactive") r.recorder.stop();
  for (const t of r.stream.getTracks()) t.stop();
  r.button.classList.remove("rec", "amber", "red");
  r.button.removeAttribute("data-beeped");
  r.button.setAttribute("aria-pressed", "false");
  r.button.setAttribute("aria-label", "Dictate");
  const dt = r.button.querySelector(".dt");
  if (dt) dt.textContent = "Writing it down…";
}

/** Sends the recording once for its words, then puts them at the cursor. Nothing is kept. */
async function finish(r: Recording) {
  const seconds = Math.min(DICTATION_MAX_SECONDS, Math.max(1, (Date.now() - r.started) / 1000));
  const blob = new Blob(r.chunks, { type: r.recorder.mimeType || "audio/webm" });
  r.chunks.length = 0;
  const dt = r.button.querySelector(".dt");
  try {
    const res = await fetch(
      `/api/ai/transcribe?seconds=${Math.round(seconds)}&module=${encodeURIComponent(moduleHere())}`,
      {
        method: "POST",
        headers: { "content-type": blob.type.split(";")[0] || "audio/webm" },
        body: blob,
      },
    );
    const body = (await res.json().catch(() => ({}))) as { text?: string; error?: string };
    if (!res.ok || !body.text) {
      toast(res.status === 409 ? PAUSED : "Nothing was written", body.error);
      return;
    }
    const put = insertDictation(r.box.value, r.caret, body.text);
    r.box.value = put.value;
    // The field app's visit note keeps whether it was dictated (brief 1D-3).
    r.box.dataset.dictated = "1";
    nextAt.set(r.box, put.caret);
    r.box.dispatchEvent(new Event("input", { bubbles: true }));
    r.box.focus();
    r.box.setSelectionRange(put.caret, put.caret);
    toast(
      "Dictation added",
      `${dictationClock(seconds).text.split(" / ")[0]} transcribed · review before saving`,
    );
  } catch {
    toast("Nothing was written", "Check the connection and try again");
  } finally {
    if (dt) dt.textContent = "";
  }
}

// ---- Writing help -------------------------------------------------------------------------------------

function closeMenus() {
  for (const m of document.querySelectorAll(".aimenu")) m.remove();
}

function openMenu(box: HTMLTextAreaElement, button: HTMLButtonElement) {
  const was = button.parentElement?.querySelector(".aimenu");
  closeMenus();
  if (was) return;
  const item = box.dataset.aiItem;
  const menu = el("div", { class: "aimenu", role: "menu", "aria-label": "AI writing help" });
  for (const a of WRITING_ACTIONS) {
    if (a.key === "reply" && !item) continue;
    const b = el("button", { type: "button", role: "menuitem" }, a.label);
    b.addEventListener("click", () => {
      closeMenus();
      void help(box, a.key);
    });
    menu.append(b);
  }
  button.parentElement?.append(menu);
  (menu.querySelector("button") as HTMLButtonElement | null)?.focus();
}

/** AI's version beside the original, with Use it and Discard. Nothing changes until Use it. */
async function help(box: HTMLTextAreaElement, action: WritingAction) {
  const wrap = box.closest(".dictwrap");
  wrap?.parentElement?.querySelector(":scope > .aiprop")?.remove();
  const original = box.value;
  const res = await fetch("/api/ai/write", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      action,
      text: original,
      module: moduleHere(),
      item: box.dataset.aiItem,
    }),
  });
  const body = (await res.json().catch(() => ({}))) as { answer?: string; error?: string };
  if (!res.ok || typeof body.answer !== "string")
    return toast(res.status === 409 ? PAUSED : "AI couldn't help with this", body.error);
  const label = WRITING_ACTIONS.find((a) => a.key === action)?.label ?? "AI";
  const use = el("button", { type: "button", class: "btn sm pri" }, icon("check"), " Use it");
  const discard = el("button", { type: "button", class: "btn sm" }, "Discard");
  const card = el(
    "div",
    { class: "aiprop aisug card", role: "region", "aria-label": "AI's version" },
    el("div", { class: "eyebrow" }, icon("sparkles-fill"), ` ${label}`),
    el(
      "div",
      { class: "aicols" },
      el(
        "div",
        {},
        el("div", { class: "small muted" }, "Original"),
        el("p", { class: "aitext" }, original || "–"),
      ),
      el(
        "div",
        {},
        el("div", { class: "small muted" }, "AI's version"),
        el("p", { class: "aitext" }, body.answer),
      ),
    ),
    el("div", { class: "actions" }, use, discard),
  );
  use.addEventListener("click", () => {
    box.value = body.answer ?? box.value;
    box.dispatchEvent(new Event("input", { bubbles: true }));
    card.remove();
    box.focus();
    toast("AI's version is in", "Review it before saving or sending");
  });
  discard.addEventListener("click", () => {
    card.remove();
    box.focus();
  });
  (wrap ?? box).after(card);
  use.focus();
}

// ---- Putting the buttons on every text box -----------------------------------------------------------

function bar(box: HTMLTextAreaElement): HTMLElement {
  const b = el("span", { class: "dictbar" });
  const paused = (button: HTMLButtonElement) => {
    button.classList.add("paused");
    button.dataset.tip = PAUSED;
    button.addEventListener("click", () => toast(PAUSED, "Work goes on by hand"));
  };
  if (shows(states.dictation)) {
    const mic = el(
      "button",
      {
        type: "button",
        class: "dictbtn",
        "aria-label": states.dictation === "paused" ? `Dictate (${PAUSED})` : "Dictate",
        "aria-pressed": "false",
        "data-tip": "Dictate · up to 10 minutes",
      },
      icon("mic"),
      el("span", { class: "dt", "aria-live": "polite" }),
    );
    if (states.dictation === "paused") paused(mic);
    else
      mic.addEventListener("click", () => {
        if (recording?.button === mic) stopRecording();
        else void startRecording(box, mic);
      });
    b.append(mic);
  }
  if (shows(states.writing)) {
    const ai = el(
      "button",
      {
        type: "button",
        class: "aibtn",
        "aria-label":
          states.writing === "paused" ? `AI writing help (${PAUSED})` : "AI writing help",
        "aria-haspopup": "menu",
        "data-tip": "AI: clean up, shorten, draft a reply, or rewrite as a clinical note",
      },
      icon("sparkles-fill"),
    );
    if (states.writing === "paused") paused(ai);
    else ai.addEventListener("click", () => openMenu(box, ai));
    b.append(ai);
  }
  return b;
}

function decorate(box: HTMLTextAreaElement) {
  let wrap = box.parentElement?.classList.contains("dictwrap") ? box.parentElement : null;
  wrap?.querySelector(":scope > .dictbar")?.remove();
  const tools = bar(box);
  if (!tools.childElementCount) {
    box.classList.remove("hasdict");
    return;
  }
  if (!wrap) {
    wrap = el("span", { class: "dictwrap" });
    box.before(wrap);
    wrap.append(box);
  }
  box.classList.add("hasdict");
  wrap.append(tools);
}

function decorateAll(root: ParentNode) {
  for (const box of root.querySelectorAll<HTMLTextAreaElement>("textarea:not([data-ai-seen])")) {
    box.dataset.aiSeen = "1";
    decorate(box);
  }
}

let started = false;

/** Called by the shell with what /api/me says; redraws the buttons on every text box. */
export function setAiStates(next: States) {
  states = next;
  if (recording && !shows(states.dictation)) stopRecording();
  for (const box of document.querySelectorAll<HTMLTextAreaElement>("textarea[data-ai-seen]"))
    decorate(box);
  if (!started) {
    started = true;
    decorateAll(document);
    new MutationObserver((changes) => {
      for (const c of changes)
        for (const n of c.addedNodes)
          if (n instanceof HTMLElement) decorateAll(n.parentElement ?? n);
    }).observe(document.body, { childList: true, subtree: true });
    document.addEventListener("click", (e) => {
      const t = e.target as Element | null;
      if (!t?.closest?.(".aimenu") && !t?.closest?.(".aibtn")) closeMenus();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && document.querySelector(".aimenu")) closeMenus();
    });
  }
}

/** Whether reading uploads is on (the person record asks before filling a credential's fields). */
export const readingState = () => states.reading;
